import {useEffect, useMemo, useReducer} from "react";


const emptyArray = [];

const useSelectedFeatures = (props={}) => {
    const {
        propertiesId='id',
        features=emptyArray,
        multiple=true,
        multiSelectKey=undefined,
        keepSelectedIfMissing=false
    } = props;

    // Store the selected feature and not only the id. This is more flexible and in the future
    // if we want to show the selectedFeature even if they are not in the features set we could do that.
    const [selectedFeatures, dispatcher] = useReducer((state, action) => {
        switch (action.type) {
            case 'toggle':
                const {feature, event} = action;
                const isMultiSelect = multiple && (multiSelectKey === undefined || (event && (
                    (multiSelectKey === 'shift' && event.originalEvent.shiftKey) ||
                    (multiSelectKey === 'ctrl' && event.originalEvent.ctrlKey) ||
                    (multiSelectKey === 'alt' && event.originalEvent.altKey) ||
                    (multiSelectKey === 'meta' && event.originalEvent.metaKey)
                )));

                const isAlreadySelected = state.find(x => x.properties[propertiesId] === feature.properties[propertiesId]);

                if (isMultiSelect) {
                    if (isAlreadySelected) {
                        return state.filter(x => x.properties[propertiesId] !== feature.properties[propertiesId]);
                    } else {
                        return [...state, feature];
                    }
                } else {
                    if (isAlreadySelected) {
                        return state.length === 1 ? [] : [feature];
                    } else {
                        return [feature];
                    }
                }
            case 'deSelect':
                return state.filter(x => x.properties[propertiesId] !== action.feature.properties[propertiesId]);
            case 'set':
                return action.features
            case 'clear':
                return [];
            default:
                throw new Error(`Unknown action type: ${action.type}`);
        }}, []);

    // When the features changes update the selectedFeature to keep only those that are still present in the new set
    useEffect(() => {
        if (!keepSelectedIfMissing) {
            const featureIdsSet = new Set(selectedFeatures.map(f => f.properties[propertiesId]))
            const newSelectedFeatures = (features ?? []).filter(f => featureIdsSet.has(f.properties[propertiesId]))
            dispatcher({type: 'set', features: newSelectedFeatures});
        }
    }, [!!features && features.length && features.length > 0 ? features : null, keepSelectedIfMissing]);

    const selectedFeatureIds = useMemo(() => {
        return selectedFeatures.map(f => f.properties[propertiesId])
    }, [selectedFeatures]);

    const isFeatureIdSelected = (featureId) => {
        return selectedFeatureIds.some(x => x === featureId);
    }

    const isFeatureSelected = (feature) => {
        return isFeatureIdSelected(feature.properties[propertiesId]);
    }

    const getSelectedFeatures = (features) => {
        const other = (features ?? []).reduce((acc, feature) => {
            if (!selectedFeatureIds.some(featureId => featureId === feature.properties[propertiesId])) {
                acc.push(feature);
            }
            return acc;
        }, []);
        return [selectedFeatures, other]
    }

    const clearSelection = () => {
        dispatcher({type: 'clear'});
    }

    const handleFeatureClick = (feature, event) => {
        dispatcher({type: 'toggle', feature: feature, event: event});
    }

    const handleFeatureDeSelect = (feature) => {
        dispatcher({type: 'deSelect', feature: feature});
    }

    const toggleFeature = (feature, event) => {
        dispatcher({type: 'toggle', feature, event});
    }

    const [notSelectedFeatures] = useMemo(
        () => getSelectedFeatures(features)[1],
        [features, selectedFeatureIds]
    );

    const emptySelection = selectedFeatureIds.length === 0;
    const selectedFeature = selectedFeatures.length > 0 ? selectedFeatures[selectedFeatures.length - 1] : null;

    return {
        selectedFeatureIds,
        selectedFeatures,
        selectedFeature: selectedFeature,
        selectedFeatureId: selectedFeature?.properties[propertiesId],
        notSelectedFeatures,
        getSelectedFeatures,
        handleFeatureDeSelect,
        toggleFeature,
        isFeatureIdSelected,
        isFeatureSelected,
        handleFeatureClick,
        emptySelection,
        clearSelection
    };
}

export default useSelectedFeatures;